<template>
  <div>
    <certificates-list-filters
      :first-name-filter.sync="firstNameFilter"
      :last-name-filter.sync="lastNameFilter"
      :valid-until-from.sync="validUntilFrom"
      :valid-until-to.sync="validUntilTo"
      :role-filter.sync="roleFilter"
      :certification-date-from.sync="certificationDateFrom"
      :certification-date-to.sync="certificationDateTo"
      :passed-filter.sync="passedFilter"
    />

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Pokaż</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>certyfikatów</label>

            <b-button
              v-if="canExportCertificationList"
              variant="gradient-primary"
              class="ml-2"
              :disabled="exportInProgress"
              @click="exportCertificationXls()"
            >
              <b-spinner
                v-if="exportInProgress"
                small
                class="mr-50"
              />
              <span class="text-nowrap">Eksport certyfikacji</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div class="table-responsive">
        <table class="table certificates-table mb-0">
          <thead>
            <tr>
              <th colspan="2">
                Rodzaj
              </th>
              <th colspan="3">
                DORADCA DS. SPRZEDAŻY
              </th>
              <th colspan="2">
                KIEROWNIK SPRZEDAŻY
              </th>
            </tr>
            <tr>
              <th>Imię i nazwisko</th>
              <th>Termin ważności certyfikatu</th>
              <th
                v-for="column in certificationColumnHeaders"
                :key="column.key"
                :class="{ 'cert-group-start': column.isGroupStart }"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td
                :colspan="tableColumnCount"
                class="text-center"
              >
                <b-spinner class="align-middle mr-1" /> Pobieranie listy certyfikatów
              </td>
            </tr>
            <tr v-else-if="!paginatedCertificates.length">
              <td
                :colspan="tableColumnCount"
                class="text-center"
              >
                Nie znaleziono certyfikatów spełniających podane kryteria.
              </td>
            </tr>
            <template v-else>
              <tr
                v-for="user in paginatedCertificates"
                :key="user.id"
              >
                <td>{{ user.firstName }} {{ user.lastName }}</td>
                <td>{{ formatValidUntil(user) }}</td>
                <td
                  v-for="cellKey in cellKeys"
                  :key="cellKey.key"
                  :class="{ 'cert-group-start': cellKey.isGroupStart }"
                >
                  <div class="cell-actions">
                    <template v-if="cellButtons(getCell(user[cellKey.path], cellKey.level), user).length">
                      <b-button
                        v-for="btn in cellButtons(getCell(user[cellKey.path], cellKey.level), user)"
                        :key="btn.key"
                        v-b-tooltip.hover.top="btn.tooltip"
                        :variant="btn.variant"
                        size="sm"
                        class="cell-action-btn"
                        :disabled="isEnrolButtonLoading(btn)"
                        @click="onCellAction(btn, user.id)"
                      >
                        <b-spinner
                          v-if="isEnrolButtonLoading(btn, user.id)"
                          small
                          class="cell-action-btn-spinner"
                        />
                        <span v-else>{{ btn.label }}</span>
                      </b-button>
                    </template>
                    <span
                      v-else
                      class="text-muted"
                    >—</span>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Widok {{ metadata.from }} - {{ metadata.to }} z {{ metadata.of }} certyfikatów
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCertificates"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-modal
      v-model="noTermsModalVisible"
      centered
      title="Brak wolnych terminów"
      ok-only
      ok-title="OK"
    >
      Obecnie nie ma wolnych terminów certyfikacji, na które można zapisać tego pracownika.
    </b-modal>

    <b-modal
      v-model="certAccessDeniedModalVisible"
      centered
      title="Brak dostępu"
      ok-only
      ok-title="OK"
    >
      Nie masz dostępu do tej certyfikacji.
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BPagination, BSpinner, BButton, BModal, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  ACTION_ENROL,
  ACTION_DETAILS,
  filterCertificatesList,
  buildCertificationStatusExportParams,
  paginateList,
  buildListMetadata,
  buildCertificatesListCellKeys,
  certificationLevelColumnLabel,
  certificatesListTableColumnCount,
  getCertificationCell,
  formatCertificateValidUntil,
  cellButtonsForCertification,
} from '@/helpers/certification/certificatesListLogic'
import { hasBookableCertificationTerms } from '@/helpers/certification/certificationEnrolLogic'
import CertificatesListFilters from './CertificatesListFilters.vue'

export default {
  components: {
    CertificatesListFilters,
    BCard,
    BRow,
    BCol,
    BPagination,
    BSpinner,
    BButton,
    BModal,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      ACTION_ENROL,
      loading: false,
      firstNameFilter: '',
      lastNameFilter: '',
      validUntilFrom: null,
      validUntilTo: null,
      roleFilter: null,
      certificationDateFrom: null,
      certificationDateTo: null,
      passedFilter: null,
      exportInProgress: false,
      perPage: 10,
      currentPage: 1,
      perPageOptions: [10, 25, 50, 100],
      noTermsModalVisible: false,
      certAccessDeniedModalVisible: false,
      enrolCheckUserId: null,
    }
  },
  computed: {
    roles() {
      return this.currentUser && this.currentUser.roles ? this.currentUser.roles : []
    },
    canExportCertificationList() {
      return ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_FORD_POLAND', 'ROLE_TRAINER']
        .some(role => this.roles.includes(role))
    },
    exportFilterParams() {
      return buildCertificationStatusExportParams({
        firstNameFilter: this.firstNameFilter,
        lastNameFilter: this.lastNameFilter,
        validUntilFrom: this.validUntilFrom,
        validUntilTo: this.validUntilTo,
        roleFilter: this.roleFilter,
        certificationDateFrom: this.certificationDateFrom,
        certificationDateTo: this.certificationDateTo,
        passedFilter: this.passedFilter,
      })
    },
    tableColumnCount() {
      return certificatesListTableColumnCount()
    },
    cellKeys() {
      return buildCertificatesListCellKeys()
    },
    certificationColumnHeaders() {
      return this.cellKeys.map(cellKey => ({
        key: cellKey.key,
        label: certificationLevelColumnLabel(cellKey.level),
        isGroupStart: cellKey.isGroupStart,
      }))
    },
    certificatesList() {
      const users = this.$store.state.user.certificationStatusList
        ? this.$store.state.user.certificationStatusList['hydra:member']
        : []
      return filterCertificatesList(users, {
        isManager: this.isManager,
        currentUserId: this.currentUser ? this.currentUser.id : null,
        firstNameFilter: this.firstNameFilter,
        lastNameFilter: this.lastNameFilter,
        validUntilFrom: this.validUntilFrom,
        validUntilTo: this.validUntilTo,
        roleFilter: this.roleFilter,
        certificationDateFrom: this.certificationDateFrom,
        certificationDateTo: this.certificationDateTo,
        passedFilter: this.passedFilter,
      })
    },
    paginatedCertificates() {
      return paginateList(this.certificatesList, this.perPage, this.currentPage)
    },
    totalCertificates() {
      return this.certificatesList.length
    },
    metadata() {
      return buildListMetadata(
        this.paginatedCertificates.length,
        this.perPage,
        this.currentPage,
        this.totalCertificates,
      )
    },
  },
  watch: {
    firstNameFilter() {
      this.currentPage = 1
    },
    lastNameFilter() {
      this.currentPage = 1
    },
    validUntilFrom() {
      this.currentPage = 1
    },
    validUntilTo() {
      this.currentPage = 1
    },
    roleFilter() {
      this.currentPage = 1
    },
    certificationDateFrom() {
      this.currentPage = 1
    },
    certificationDateTo() {
      this.currentPage = 1
    },
    passedFilter() {
      this.currentPage = 1
    },
    perPage() {
      this.currentPage = 1
    },
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.loading = true
      this.$store.dispatch('user/fetchCertificationStatusList').finally(() => { this.loading = false })
    },
    getCell(cells, level) {
      return getCertificationCell(cells, level)
    },
    formatValidUntil(user) {
      return formatCertificateValidUntil(user)
    },
    cellButtons(cell, rowUser) {
      return cellButtonsForCertification(cell, rowUser, { isManager: this.isManager })
    },
    isEnrolButtonLoading(btn, rowUserId) {
      return btn.action === ACTION_ENROL
        && this.enrolCheckUserId != null
        && String(this.enrolCheckUserId) === String(rowUserId)
    },

    onCellAction(button, rowUserId) {
      if (button.action === ACTION_ENROL) {
        if (!this.isManager) {
          return
        }
        this.openEnrolmentForUser(rowUserId)
        return
      }

      if (button.action === ACTION_DETAILS) {
        if (!button.certificationId) {
          this.notImplemented('Brak identyfikatora certyfikacji — odśwież listę lub skontaktuj się z administratorem.')
          return
        }
        if (button.canViewDetails === false) {
          this.certAccessDeniedModalVisible = true
          return
        }
        this.$router.push({
          name: 'certifications-detail',
          params: { id: String(button.certificationId) },
          ...(button.detailTab ? { query: { tab: button.detailTab } } : {}),
        })
      }
    },
    openEnrolmentForUser(userId) {
      if (this.enrolCheckUserId != null) {
        return
      }
      this.enrolCheckUserId = userId
      this.$store.dispatch('certification/fetchEnrolmentOptions', {
        userId,
        date: null,
      }).then(response => {
        if (response && response.status === 200) {
          if (hasBookableCertificationTerms(response.data)) {
            this.$router.push({
              name: 'certifications-enrol',
              params: { userId: String(userId) },
            })
          } else {
            this.noTermsModalVisible = true
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się sprawdzić dostępnych terminów',
              text: response && response.data && response.data['hydra:description']
                ? response.data['hydra:description']
                : null,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        }
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Nie udało się sprawdzić dostępnych terminów',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.enrolCheckUserId = null
      })
    },

    notImplemented(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Funkcja w przygotowaniu',
          icon: 'InfoIcon',
          variant: 'info',
          text: message,
        },
      })
    },

    async exportCertificationXls() {
      if (this.exportInProgress) {
        return
      }
      this.exportInProgress = true
      try {
        const response = await this.$http.get('/users/certification_status/export', {
          params: this.exportFilterParams,
          responseType: 'blob',
        })
        if (response.status === 200) {
          const blob = new Blob([response.data, 'application/vnd.ms-excel'])
          this.downloadBlob(blob, 'certyfikacja.xls')
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Nie udało się wyeksportować listy certyfikacji',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.exportInProgress = false
      }
    },
    downloadBlob(blob, name = 'file.txt') {
      if (
        window.navigator
        && window.navigator.msSaveOrOpenBlob
      ) return window.navigator.msSaveOrOpenBlob(blob)

      const data = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = data
      link.download = name

      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      )

      setTimeout(() => {
        window.URL.revokeObjectURL(data)
        link.remove()
      }, 100)
      return 1
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.cell-action-btn-spinner {
  width: 0.9rem;
  height: 0.9rem;
  vertical-align: middle;
}

.certificates-table {
  th,
  td {
    vertical-align: middle;
    white-space: nowrap;
    border-left: 1px solid #ebe9f1;
    padding: 0.5rem 0.75rem;

    &:first-child {
      border-left: 0;
    }
  }

  thead tr:first-child th {
    text-align: center;
    border-bottom-width: 1px;
  }

  tbody tr:last-child td {
    border-bottom: 1px solid #ebe9f1;
  }

  // Ciemniejsze separatory między grupami: Rodzaj | DORADCA DS. SPRZEDAŻY | KIEROWNIK SPRZEDAŻY.
  thead tr:first-child th:not(:first-child),
  th.cert-group-start,
  td.cert-group-start {
    border-left-width: 1px;
    border-left-color: #b9b9c3;
  }
}

.cell-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.cell-action-btn {
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  white-space: nowrap;
  line-height: 1.1;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
